import LoadError from "../components/LoadError";
import useActionLock from "../hooks/useActionLock";
import { formatMoney } from "../utils/format";
import { useConfirm } from "../components/ConfirmProvider";
import { Clock, Edit, Package, Plus, Trash2 } from "lucide-react";
import React, { useEffect, useState } from "react";

import PrestationForm from "../components/PrestationForm";
import { prestationsAPI } from "../services/api";
import toast from "react-hot-toast";

const Prestations = () => {
  const confirm = useConfirm();
  const [actionBusy, runAction] = useActionLock();
  const [loadError, setLoadError] = useState("");
  const [search, setSearch] = useState("");
  const [prestations, setPrestations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPrestation, setEditingPrestation] = useState(null);

  useEffect(() => {
    loadPrestations();
  }, []);

  const loadPrestations = async () => {
    setLoadError("");
    try {
      const response = await prestationsAPI.getAll();
      setPrestations(response.data);
    } catch (error) {
      setLoadError("Impossible de charger les prestations. Réessayez.");
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePrestation = (prestationData) => runAction(async () => {
    try {
      await prestationsAPI.create(prestationData);
      toast.success("Prestation créée avec succès");
      setShowForm(false);
      loadPrestations();
    } catch (error) {
      toast.error("Erreur lors de la création de la prestation");
      throw error;
    }
 });

  const handleUpdatePrestation = (prestationData) => runAction(async () => {
    try {
      await prestationsAPI.update(editingPrestation.id, prestationData);
      toast.success("Prestation mise à jour avec succès");
      closeForm();
      loadPrestations();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour de la prestation");
      throw error;
    }
 });

  const handleDeletePrestation = (prestationId) => runAction(async () => {
    if (
      await confirm({ title: "Supprimer la prestation ?", description: "Cette suppression est définitive. Vérifiez l’élément sélectionné avant de continuer.", confirmLabel: "Supprimer", danger: true, details: [["Prestation", prestations.find((entry) => entry.id === prestationId)?.description]] })
    ) {
      try {
        await prestationsAPI.delete(prestationId);
        toast.success("Prestation supprimée avec succès");
        loadPrestations();
      } catch (error) {
        toast.error("Erreur lors de la suppression de la prestation");
      }
    }
 });

  const openEditForm = (prestation) => {
    setEditingPrestation(prestation);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingPrestation(null);
  };

  const filteredPrestations = prestations.filter((entry) => String(entry.description || "").toLocaleLowerCase("fr").includes(search.trim().toLocaleLowerCase("fr")));

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Prestations fréquentes
          </h1>
          <p className="text-gray-600">
            Gérez vos prestations récurrentes pour les réutiliser rapidement
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="btn-primary flex w-full items-center justify-center gap-2 sm:w-auto"
        >
          <Plus className="h-5 w-5" />
          <span>Nouvelle prestation</span>
        </button>
      </div>

      <label className="block text-sm">Rechercher une prestation<input className="input-field mt-1 max-w-lg" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Description" /></label>
      <LoadError message={loadError} onRetry={loadPrestations} />
      <p role="status" className="text-sm text-gray-500">{filteredPrestations.length} résultat(s)</p>
      {filteredPrestations.length === 0 && !loadError && prestations.length > 0 && <div className="card"><p>Aucune prestation ne correspond à cette recherche.</p><button className="btn-secondary mt-3" onClick={() => setSearch("")}>Effacer la recherche</button></div>}
      {/* Liste des prestations */}
      <div className="card">
        {prestations.length === 0 && !loadError ? (
          <div className="text-center py-12">
            <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Aucune prestation
            </h3>
            <p className="text-gray-600 mb-4">
              Ajoutez vos premières prestations pour les réutiliser
            </p>
            <button onClick={() => setShowForm(true)} className="btn-primary">
              Ajouter une prestation
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPrestations.map((prestation) => (
              <div
                key={prestation.id}
                className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <Package className="h-5 w-5 text-green-600" />
                    </div>
                    <div className="ml-3">
                      <h3 className="font-semibold text-gray-900">
                        {prestation.description}
                      </h3>
                      <p className="text-sm text-gray-600 flex items-center">
                        <Clock className="h-4 w-4 mr-1" />
                        {prestation.unite}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => openEditForm(prestation)}
                      className="text-blue-600 hover:text-blue-900 p-2 min-h-[44px] min-w-[44px]"
                      title="Modifier"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      disabled={actionBusy} onClick={() => handleDeletePrestation(prestation.id)}
                      className="text-red-600 hover:text-red-900 p-2 min-h-[44px] min-w-[44px]"
                      title="Supprimer"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center justify-between">
                    <span>Quantité par défaut :</span>
                    <span className="font-medium">{prestation.quantite}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Prix unitaire :</span>
                    <span className="font-medium">
                      {formatMoney(prestation.prix_unitaire, prestation.devise)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                    <span className="font-medium">Total :</span>
                    <span className="font-bold text-primary-600">
                      {formatMoney(prestation.quantite * prestation.prix_unitaire, prestation.devise)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Formulaire prestation */}
      {showForm && (
        <PrestationForm
          prestation={editingPrestation}
          onSubmit={
            editingPrestation ? handleUpdatePrestation : handleCreatePrestation
          }
          onCancel={closeForm}
        />
      )}
    </div>
  );
};

export default Prestations;
